var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { reaction, observable, computed } from "mobx";
import { Persistence, SocialLoginAuthState } from "../Infra/Persistence";
import { NamedRoutes } from "./NamedRoutes";
import { LoginAnalyticsEvent } from "healthex-common/analytics.dtos";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { generateOAuthState, getAnonymousUserId, LoginFlow } from "../Logic/AuthManager";
import { GFIDProvider, isSocialLoginGfidProvider } from "healthex-common/healthcareorganization.dtos";
import { asEnum, willNeverHappen } from "healthex-common/utils";
import { isOk } from "healthex-common/result";
import { effectiveQueryParams } from "./queryParams";
import { getObservableT } from "../Infra/i18next";
const t = getObservableT("patients");
export class SocialLoginAuthManager {
    constructor(authManager, persistence, routingUIManager, analyticsService, environment, verificationService, bannerUIManager) {
        this.authManager = authManager;
        this.persistence = persistence;
        this.routingUIManager = routingUIManager;
        this.analyticsService = analyticsService;
        this.environment = environment;
        this.verificationService = verificationService;
        this.bannerUIManager = bannerUIManager;
        this.isCompletingFlow = false;
        this.state = SocialLoginAuthState.INACTIVE;
        this._errorMessage = null;
        this.requestedGFIDProvider = null;
        this.responseGFIDProvider = null;
        this.pendingSocialLoginData = null;
        this.pendingSocialLoginData = this.persistence.getPendingSocialLoginData();
        reaction(() => this.routingUIManager.currentRoute, (currentRoute) => {
            if ((currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.routeName) === NamedRoutes.SOCIAL_LOGIN_CALLBACK.routeName) {
                void this.processSocialLoginCallback();
            }
        }, { fireImmediately: true, name: "SocialLoginAuthManager_currentRoute" });
        reaction(() => this.authManager.isLoggedIn, (isLoggedIn) => {
            if (isLoggedIn && !this.isCompletingFlow) {
                this.resetAuth();
            }
        }, { name: "SocialLoginAuthManager_isLoggedIn" });
        window.addEventListener("pageshow", (event) => {
            if (event.persisted) {
                this.resetAuthStateIfNeeded();
            }
        });
    }
    get errorMessage() {
        return this.state === SocialLoginAuthState.ERROR ? this._errorMessage : null;
    }
    get isAccountLinkingError() {
        return this.state === SocialLoginAuthState.ERROR && this._errorMessage === "CLEAR_ACCOUNT_LINKING_REQUIRED";
    }
    get hasPendingSocialLogin() {
        return !!this.pendingSocialLoginData;
    }
    get pendingPhoneVerified() {
        if (this.pendingSocialLoginData) {
            return !!this.pendingSocialLoginData.phoneVerified;
        }
        return null;
    }
    get shouldShowAccountSetupScreen() {
        const pending = this.pendingSocialLoginData;
        if (!pending)
            return null;
        return pending.showAccountSetup && this.authManager.linkingStatus === null;
    }
    get hasCompletedAuth() {
        return this.state === SocialLoginAuthState.SUCCESS || this.state === SocialLoginAuthState.ERROR;
    }
    async redirectToSocialLoginPage(callbackHash, gfidProvider, source, organizationId) {
        var _a, _b;
        this.state = SocialLoginAuthState.IN_PROGRESS;
        this.requestedGFIDProvider = gfidProvider;
        const heapGlobalEventProperties = { ...getHeapGlobalEventProperties(), source, provider: gfidProvider };
        const userId = (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined;
        const identity = (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined;
        void this.analyticsService.trackHeapEvent({
            event: LoginAnalyticsEvent.LOGIN_ATTEMPTED,
            identity,
            user_id: userId,
            properties: { ...heapGlobalEventProperties },
        });
        const resolvedOrganizationId = typeof organizationId === "function" ? await organizationId() : organizationId;
        const url = this.getSocialLoginUrl(gfidProvider);
        if (!url) {
            this.handleAuthError();
            return;
        }
        const state = generateOAuthState();
        this.setSocialLoginAuthData({
            callbackHash,
            state,
            heapGlobalEventProperties,
            organizationId: resolvedOrganizationId !== null && resolvedOrganizationId !== void 0 ? resolvedOrganizationId : undefined,
        });
        window.location.href = `${url}?state=${state}`;
    }
    static checkAndReturnSocialLoginAuthResponse() {
        var _a;
        const socialLoginAuthData = new Persistence().getSocialLoginAuthData();
        if (!socialLoginAuthData)
            return null;
        const queryParams = effectiveQueryParams();
        const gfidProvider = asEnum(GFIDProvider)((_a = queryParams.get("social-login-provider")) !== null && _a !== void 0 ? _a : "");
        const state = queryParams.get("state");
        const token = queryParams.get("token");
        const error = queryParams.get("error");
        const linkingToken = queryParams.get("linkingToken");
        const isFirstTimeLogin = queryParams.get("isFirstTimeLogin") === "true";
        const verificationKey = queryParams.get("verificationKey");
        if (!!gfidProvider && !!state && isSocialLoginGfidProvider(gfidProvider) && (!!token || !!error || !!verificationKey)) {
            return {
                gfidProvider,
                token,
                error,
                linkingToken,
                state,
                isFirstTimeLogin,
                verificationKey,
            };
        }
        else {
            return null;
        }
    }
    resetAuth() {
        this.state = SocialLoginAuthState.INACTIVE;
        this.requestedGFIDProvider = null;
        this.persistence.setSocialLoginAuthData(null);
        this.clearPendingSocialLogin();
    }
    clearPendingSocialLogin() {
        this.pendingSocialLoginData = null;
        this.persistence.setPendingSocialLoginData(null);
    }
    async completeVerificationFlow(demographics, onBeforeClear) {
        var _a, _b, _c, _d, _e;
        const pendingData = this.pendingSocialLoginData;
        const stash = this.persistence.getConsentDeferredVerificationStash();
        const verificationKey = (_a = pendingData === null || pendingData === void 0 ? void 0 : pendingData.verificationKey) !== null && _a !== void 0 ? _a : stash === null || stash === void 0 ? void 0 : stash.verificationKey;
        const gfidProvider = (_b = pendingData === null || pendingData === void 0 ? void 0 : pendingData.gfidProvider) !== null && _b !== void 0 ? _b : stash === null || stash === void 0 ? void 0 : stash.gfidProvider;
        if (!verificationKey || !gfidProvider) {
            return { ok: false, code: "no_pending_data" };
        }
        const userId = (_c = getAnonymousUserId()) !== null && _c !== void 0 ? _c : undefined;
        const heapProperties = {
            ...getHeapGlobalEventProperties(),
            ...((_d = pendingData === null || pendingData === void 0 ? void 0 : pendingData.heapGlobalEventProperties) !== null && _d !== void 0 ? _d : {}),
            provider: gfidProvider,
        };
        const result = await this.verificationService.completeVerification(verificationKey, demographics.firstName, demographics.lastName, demographics.dateOfBirth, demographics.phone);
        if (!result.ok) {
            void this.analyticsService.trackHeapEvent({
                event: LoginAnalyticsEvent.LOGIN_ERROR,
                user_id: userId,
                properties: heapProperties,
            });
            this.handleCompletionError();
            return { ok: false, code: result.code };
        }
        this.isCompletingFlow = true;
        try {
            await this.authManager.handleSocialLogin(result.token, this.getLoginFlow(gfidProvider), result.isFirstTimeLogin);
            await (onBeforeClear === null || onBeforeClear === void 0 ? void 0 : onBeforeClear());
            this.clearPendingSocialLogin();
            this.persistence.setConsentDeferredVerificationStash(null);
        }
        finally {
            this.isCompletingFlow = false;
        }
        void this.analyticsService.trackHeapEvent({
            event: LoginAnalyticsEvent.LOGIN_SUCCESS,
            identity: (_e = this.authManager.id) !== null && _e !== void 0 ? _e : undefined,
            user_id: userId,
            properties: heapProperties,
        });
        if (this.authManager.id && userId) {
            void this.analyticsService.identifyUser(this.authManager.id, userId);
        }
        return { ok: true, isFirstTimeLogin: result.isFirstTimeLogin };
    }
    handleCompletionError() {
        this.bannerUIManager.displayBanner({
            type: "ERROR",
            text: t("Something went wrong completing your login. Please try again."),
            durationInMS: 6000,
        });
        this.clearPendingSocialLogin();
        this.persistence.setConsentDeferredVerificationStash(null);
    }
    setSocialLoginAuthData(socialLoginAuthData) {
        this.persistence.setSocialLoginAuthData(socialLoginAuthData);
    }
    getSocialLoginAuthData() {
        return this.persistence.getSocialLoginAuthData();
    }
    async processSocialLoginCallback() {
        var _a, _b;
        const socialLoginAuthData = this.getSocialLoginAuthData();
        if (!socialLoginAuthData) {
            this.handleAuthError();
            return;
        }
        const socialLoginAuthResponse = SocialLoginAuthManager.checkAndReturnSocialLoginAuthResponse();
        if (!socialLoginAuthResponse) {
            this.handleAuthError();
            return;
        }
        if (!socialLoginAuthData.state || !socialLoginAuthResponse.state || socialLoginAuthData.state !== socialLoginAuthResponse.state) {
            this.handleAuthError();
            return;
        }
        const { token, gfidProvider, error, linkingToken, verificationKey } = socialLoginAuthResponse;
        this.responseGFIDProvider = gfidProvider;
        const userId = (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined;
        if (error) {
            this.handleAuthError(error, linkingToken, gfidProvider);
            void this.analyticsService.trackHeapEvent({
                event: LoginAnalyticsEvent.LOGIN_ERROR,
                user_id: userId,
                properties: { ...socialLoginAuthData.heapGlobalEventProperties, error },
            });
            window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
            return;
        }
        if (verificationKey) {
            window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
            const oauthState = socialLoginAuthData.state;
            if (!oauthState) {
                this.handleAuthError();
                return;
            }
            const fetchResult = await this.verificationService.fetchPendingData(verificationKey, oauthState);
            if (!fetchResult.ok) {
                this.handleAuthError();
                return;
            }
            const pendingData = {
                verificationKey,
                email: fetchResult.email,
                firstName: fetchResult.firstName,
                lastName: fetchResult.lastName,
                dateOfBirth: fetchResult.dateOfBirth,
                phoneNumber: fetchResult.phoneNumber,
                phoneVerified: fetchResult.phoneVerified,
                isExistingUser: fetchResult.isExistingUser,
                gfidProvider: fetchResult.gfidProvider,
                showAccountSetup: fetchResult.showAccountSetup,
                heapGlobalEventProperties: socialLoginAuthData.heapGlobalEventProperties,
            };
            this.pendingSocialLoginData = pendingData;
            this.persistence.setPendingSocialLoginData(pendingData);
            this.handleAuthSuccess();
            return;
        }
        if (!token) {
            this.handleAuthError();
            window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
            return;
        }
        try {
            const result = await this.authManager.handleSocialLogin(token, this.getLoginFlow(gfidProvider), socialLoginAuthResponse.isFirstTimeLogin);
            if (isOk(result)) {
                this.handleAuthSuccess();
                void this.analyticsService.trackHeapEvent({
                    event: LoginAnalyticsEvent.LOGIN_SUCCESS,
                    identity: (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined,
                    user_id: userId,
                    properties: { ...socialLoginAuthData.heapGlobalEventProperties },
                });
                if (this.authManager.id && userId) {
                    void this.analyticsService.identifyUser(this.authManager.id, userId);
                }
            }
            else {
                this.handleAuthError();
                void this.analyticsService.trackHeapEvent({
                    event: LoginAnalyticsEvent.LOGIN_ERROR,
                    user_id: userId,
                    properties: { ...socialLoginAuthData.heapGlobalEventProperties },
                });
            }
        }
        catch (e) {
            console.error(`Error authenticating with Social Login ${gfidProvider}`, e);
            this.handleAuthError();
            void this.analyticsService.trackHeapEvent({
                event: LoginAnalyticsEvent.LOGIN_ERROR,
                user_id: userId,
                properties: { ...socialLoginAuthData.heapGlobalEventProperties },
            });
        }
        window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
    }
    handleAuthError(error, linkingToken, gfidProvider) {
        this.state = SocialLoginAuthState.ERROR;
        this._errorMessage = error !== null && error !== void 0 ? error : null;
        if (linkingToken) {
            this.setAccountLinkingData({ linkingToken, gfidProvider });
        }
    }
    handleAuthSuccess() {
        this.state = SocialLoginAuthState.SUCCESS;
    }
    resetAuthStateIfNeeded() {
        const socialLoginAuthData = this.getSocialLoginAuthData();
        const pendingSocialLoginData = this.persistence.getPendingSocialLoginData();
        if (!!socialLoginAuthData || !!pendingSocialLoginData) {
            this.resetAuth();
        }
    }
    getSocialLoginUrl(gfidProvider) {
        if (gfidProvider === GFIDProvider.GOOGLE) {
            return `${this.environment.API_BASE_URL}/auth/google`;
        }
        else if (gfidProvider === GFIDProvider.MICROSOFT) {
            return `${this.environment.API_BASE_URL}/auth/microsoft`;
        }
        else if (gfidProvider === GFIDProvider.APPLE) {
            return `${this.environment.API_BASE_URL}/auth/apple`;
        }
        else if (gfidProvider === GFIDProvider.SMART_ON_FHIR ||
            gfidProvider === GFIDProvider.CLEAR ||
            gfidProvider === GFIDProvider.HX_TEST) {
            return null;
        }
        else {
            willNeverHappen(gfidProvider);
            return null;
        }
    }
    getLoginFlow(gfidProvider) {
        if (gfidProvider === GFIDProvider.GOOGLE) {
            return LoginFlow.GFID_GOOGLE;
        }
        else if (gfidProvider === GFIDProvider.MICROSOFT) {
            return LoginFlow.GFID_MICROSOFT;
        }
        else if (gfidProvider === GFIDProvider.APPLE) {
            return LoginFlow.GFID_APPLE;
        }
        else if (gfidProvider === GFIDProvider.SMART_ON_FHIR ||
            gfidProvider === GFIDProvider.CLEAR ||
            gfidProvider === GFIDProvider.HX_TEST) {
            return null;
        }
        else {
            willNeverHappen(gfidProvider);
            return null;
        }
    }
    setAccountLinkingData(accountLinkingData) {
        this.persistence.setAccountLinkingData(accountLinkingData);
    }
    getAccountLinkingData() {
        return this.persistence.getAccountLinkingData();
    }
}
__decorate([
    observable
], SocialLoginAuthManager.prototype, "state", void 0);
__decorate([
    observable
], SocialLoginAuthManager.prototype, "_errorMessage", void 0);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "errorMessage", null);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "isAccountLinkingError", null);
__decorate([
    observable
], SocialLoginAuthManager.prototype, "requestedGFIDProvider", void 0);
__decorate([
    observable
], SocialLoginAuthManager.prototype, "responseGFIDProvider", void 0);
__decorate([
    observable
], SocialLoginAuthManager.prototype, "pendingSocialLoginData", void 0);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "hasPendingSocialLogin", null);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "pendingPhoneVerified", null);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "shouldShowAccountSetupScreen", null);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "hasCompletedAuth", null);
